/**
 * Prism interop for the Blazing.UI sample app.
 *
 * Blazor renders code panels after the page has loaded (and tab panels render lazily), so Prism's
 * automatic on-load highlighting never sees them. These helpers let a component re-highlight a
 * specific `<code>` element once it is in the DOM.
 *
 * The empty export list below is what makes this a module rather than a global script: with no
 * import or export of its own, `isolatedModules` would otherwise reject the file, and a module is
 * also what a `declare global` augmentation below requires. It introduces no exported name, so the
 * object this file publishes on `window` is unchanged.
 */

export {};

declare global {
    interface Window {
        /**
         * The vendored Prism bundle, published as a global by its own `<script>` tag. Optional
         * because a host page could load this module without also loading Prism.
         */
        Prism?: {
            /** The loaded grammars, keyed by language id. */
            languages: Record<string, PrismGrammar | undefined>;

            /**
             * Highlights a single element's contents in place.
             *
             * @param element The element whose contents to highlight.
             */
            highlightElement(element: Element): void;

            /** Highlights every Prism code block currently in the document. */
            highlightAll(): void;
        };

        /** Highlighting helpers, addressed from `CodeBlock.razor` by name. */
        prismInterop: {
            /**
             * Highlights a single `<code>` element (passed as a Blazor ElementReference).
             *
             * @param element The element to highlight.
             */
            highlightElement(element: Element): void;

            /** Highlights every Prism code block currently in the document. */
            highlightAll(): void;
        };
    }
}

/** One Prism token rule: a pattern plus the grammar its match is tokenised with. */
interface PrismToken {
    pattern: RegExp;
    lookbehind?: boolean;
    greedy?: boolean;
    inside?: PrismGrammar;
}

/** A Prism grammar: token rules keyed by token name, one rule or several per name. */
type PrismGrammar = Record<string, PrismToken | PrismToken[] | RegExp | undefined>;

/**
 * An `@code` or `@functions` section that runs to the end of the file. Prism's Razor grammar matches these
 * sections by counting braces and gives up three levels deep, so a method holding a nested `if` leaves the
 * whole section as plain text. A component's code section is normally the last thing in its file, so taking
 * everything up to the final closing brace needs no counting at all.
 */
const trailingCodeSection = /(^|[^@])@(?:code|functions)\s*\{[\s\S]*\}(?=\s*$)/;

let isRazorExtended = false;

/**
 * Teaches the Razor grammar to recognise a trailing code section at any nesting depth. The section is tried
 * first and tokenised with the grammar's own C# rules; a section followed by more markup still falls through
 * to the built-in, depth-limited rule.
 */
function extendRazorGrammar(): void {
    const razor = window.Prism?.languages["razor"];
    const block = razor?.["block"];
    if (isRazorExtended || !razor || !block || block instanceof RegExp) {
        return;
    }

    const builtIn = Array.isArray(block) ? block : [block];
    const inside = builtIn[0]?.inside;
    if (!inside) {
        return;
    }

    razor["block"] = [{ pattern: trailingCodeSection, lookbehind: true, greedy: true, inside }, ...builtIn];
    isRazorExtended = true;
}

window.prismInterop = {
    /**
     * Highlights a single `<code>` element (passed as a Blazor ElementReference).
     *
     * @param element The element to highlight.
     */
    highlightElement(element: Element): void {
        if (element && window.Prism) {
            extendRazorGrammar();
            window.Prism.highlightElement(element);
        }
    },

    /** Highlights every Prism code block currently in the document. */
    highlightAll(): void {
        if (window.Prism) {
            extendRazorGrammar();
            window.Prism.highlightAll();
        }
    }
};
